import React, { FC, ComponentType } from 'react';
import { Route, RouteProps } from 'react-router-dom';
import Spinner from '@fenderdigital/ui/Spinner';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { PLAY_AUTH_URL } from '../../lib/cognito';
import { getFlag } from '../../lib/feature-flags';
import { isChromelessRoute } from '../../utils/layout-utils';

interface PlayAuthRedirectRouteProps extends RouteProps {
  Component: ComponentType<any>;
}

const explicitRedirectRoutes = [
  'bundles-ineligible',
  'checkout',
  'chord-challenge',
  'course',
  'favorites',
  'lesson',
  'my-progress',
  'path',
  'plans',
  'practice',
  'quiz',
  'redeem',
  'tab-viewer',
  'up-next',
  'welcome-screen',
];

export const handleRedirect = (url: string): void => {
  window.location.href = url;
};

const PlayAuthRedirectRoute: FC<PlayAuthRedirectRouteProps> = ({
  Component,
  children,
  location,
  ...rest
}) => {
  const { hasCheckedSession, hasSubscription, user } = usePlayUser();
  const isPlayAuthRedirectEnabled = getFlag('isPlayAuthRedirectEnabled');
  const isFakeRedirect = process.env.REACT_APP_FAKE_REDIRECT === 'true';

  if (!hasCheckedSession) return <Spinner fullPage />;

  const isLoggedIn = user && hasCheckedSession;
  const isExplicitRedirectRoute = isChromelessRoute(location?.pathname, explicitRedirectRoutes);

  if (isPlayAuthRedirectEnabled) {
    // do the direct, if a user has a valid sub and auth'd, or if a match is
    // returned from the explicit redirect list. we want to ensure
    // users are redirected even if they aren't auth'd, but land on a specific route.
    if ((hasSubscription && isLoggedIn) || isExplicitRedirectRoute) {
      // construct the url to play auth including pathname and any query params
      const redirectUrl = `${PLAY_AUTH_URL}${location?.pathname}${location?.search}`;
      // this fake redirect is set up, for dev environment to test redirects in cypress
      if (isFakeRedirect) return <div data-id="fake-redirect">{redirectUrl}</div>;
      handleRedirect(redirectUrl);
      return <Spinner fullPage />;
    }
  }

  return (
    <Route
      {...rest}
      render={({ location: loc, history, match }): JSX.Element => {
        return <Component location={loc} history={history} match={match} />;
      }}
    />
  );
};

export default PlayAuthRedirectRoute;
